/**
 * Canonical list of every audio slot the client knows how to trigger.
 * Shared between server (seeds + admin UI) and client (AudioService).
 *
 *   - `key` identifies the slot. Code calls `AudioService.play('ui.tap')`.
 *   - `category` groups the slot in the admin upload page.
 *   - `loop` flags background-music slots that play on repeat.
 *   - `label` is what the admin sees.
 *   - `defaultVolume` is the seeded mix level; the admin can override.
 */
export type AudioCategory = 'ui' | 'sfx' | 'match' | 'bgm';

export interface AudioKeySpec {
  key: string;
  category: AudioCategory;
  label: string;
  defaultVolume: number;
  loop?: boolean;
}

export const AUDIO_KEYS: AudioKeySpec[] = [
  // ── UI taps + notifications ─────────────────────────────────────────
  { key: 'ui.tap', category: 'ui', label: 'Button tap (light)', defaultVolume: 0.5 },
  { key: 'ui.tap_primary', category: 'ui', label: 'Primary button tap', defaultVolume: 0.65 },
  { key: 'ui.error', category: 'ui', label: 'Error / invalid action', defaultVolume: 0.55 },
  { key: 'ui.toggle', category: 'ui', label: 'Toggle switch', defaultVolume: 0.45 },
  { key: 'ui.notify_friend_request', category: 'ui', label: 'Friend request received', defaultVolume: 0.75 },
  { key: 'ui.notify_room_invite', category: 'ui', label: 'Room invite received', defaultVolume: 0.8 },
  { key: 'ui.notify_dm', category: 'ui', label: 'DM received', defaultVolume: 0.6 },
  { key: 'ui.message_send', category: 'ui', label: 'Chat message sent', defaultVolume: 0.35 },
  { key: 'ui.coin_pickup', category: 'ui', label: 'Coins earned', defaultVolume: 0.7 },
  { key: 'ui.level_up', category: 'ui', label: 'XP level-up', defaultVolume: 0.85 },
  { key: 'ui.rank_up', category: 'ui', label: 'Rank promotion', defaultVolume: 0.9 },
  { key: 'ui.shop_purchase', category: 'ui', label: 'Purchase confirmed', defaultVolume: 0.7 },

  // ── Match SFX ───────────────────────────────────────────────────────
  { key: 'match.found', category: 'match', label: 'Opponent found', defaultVolume: 0.8 },
  { key: 'match.countdown', category: 'match', label: 'Countdown tick (3-2-1)', defaultVolume: 0.7 },
  { key: 'match.go', category: 'match', label: 'GO! horn', defaultVolume: 0.85 },
  { key: 'match.tap_correct', category: 'match', label: 'Correct color tap', defaultVolume: 0.45 },
  { key: 'match.tap_wrong', category: 'match', label: 'Wrong color tap', defaultVolume: 0.55 },
  { key: 'match.combo_3', category: 'match', label: 'Combo x3', defaultVolume: 0.6 },
  { key: 'match.combo_milestone', category: 'match', label: 'Combo x5 / x10 milestone', defaultVolume: 0.75 },
  { key: 'match.skill_disrupt', category: 'match', label: 'Disrupt cast', defaultVolume: 0.7 },
  { key: 'match.skill_shield', category: 'match', label: 'Shield cast', defaultVolume: 0.7 },
  { key: 'match.skill_absorbed', category: 'match', label: 'Shield absorbs disrupt', defaultVolume: 0.7 },
  { key: 'match.fall', category: 'match', label: 'Climber falls', defaultVolume: 0.7 },
  { key: 'match.bell', category: 'match', label: 'Finish bell', defaultVolume: 0.9 },
  { key: 'match.victory', category: 'match', label: 'Victory fanfare', defaultVolume: 0.9 },
  { key: 'match.defeat', category: 'match', label: 'Defeat sting', defaultVolume: 0.7 },

  // ── BGM (looped) ────────────────────────────────────────────────────
  { key: 'bgm.app', category: 'bgm', label: 'App background music', defaultVolume: 0.45, loop: true },
  { key: 'bgm.lobby', category: 'bgm', label: 'Room lobby music', defaultVolume: 0.4, loop: true },
  { key: 'bgm.matchmaking', category: 'bgm', label: 'Matchmaking music', defaultVolume: 0.5, loop: true },
  { key: 'bgm.match', category: 'bgm', label: 'In-match music', defaultVolume: 0.6, loop: true },
];

export type AudioKey = (typeof AUDIO_KEYS)[number]['key'];

export const AUDIO_KEYS_BY_KEY: Record<string, AudioKeySpec> = Object.fromEntries(
  AUDIO_KEYS.map((s) => [s.key, s]),
);
